import React from 'react';
import PropTypes from 'prop-types/prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Panel from '../../Panel';
import KeyValueEditRow from '../../KeyValueEditRow';
import TextLink from '../../TextLink';
import { useDispatchDateDeleteUser } from '../../../Redux/Action/ObjectAction';
import TeamMonteurPartnerRow from '../../TeamMonteurPartner/TeamMonteurPartnerRow';
import SelectUserTeamPartner from '../../SelectUserTeamPartner';
import ObjectDateStatusBadge from '../ObjectDateStatusBadge';
import DispoInformation from './DispoInformation';
import {
  useAccessCanAssignObjects,
  useAccessCanScheduleObjects,
  useAccessIsObjectEditable,
} from '../../../Library/AccessChecks';
import { OBJECT_DATE_TYPE_DELIVERY } from '../../../Library/Types';
import { ORDERSTATE_PARTNER_CREATED } from '../../../Library/StateTypes';

/**
 * AssignDeliveryContainer()
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
export default function AssignDeliveryContainer(props) {
  const {
    object, onUserAdd, onTeamAdd, onPartnerAdd, onUpdate,
  } = props;
  const { client } = useSelector((state) => state.client);
  const { order } = useSelector((state) => state.order);
  const { deliveryReport } = useSelector((state) => state.reports);

  const dispatchDateDeleteUser = useDispatchDateDeleteUser();

  const isEditable = useAccessIsObjectEditable(object);
  const isAssignable = useAccessCanAssignObjects();
  const isTerminable = useAccessCanScheduleObjects();

  const [canUsePartners, setCanUsePartners] = React.useState(false);
  const [allowEdit, setAllowEdit] = React.useState(false);
  const [showSelect, setShowSelect] = React.useState(false);
  const [maxDate, setMaxDate] = React.useState(null);
  const [montageDate, setMontageDate] = React.useState(null);
  const [deliveryDate, setDeliveryDate] = React.useState(null);

  React.useEffect(() => {
    setMontageDate(object.montageDate);
    setDeliveryDate(object.deliveryDate);
  }, [object]);

  React.useEffect(() => {
    if (montageDate && montageDate.date) {
      setMaxDate(montageDate.date);
    } else {
      setMaxDate(null);
    }
  }, [montageDate]);

  React.useEffect(() => {
    if (deliveryDate) {
      setCanUsePartners(
        (client.clientId === order.clientId && (!deliveryDate.users || deliveryDate.users.length === 0)),
      );
      setAllowEdit((client.clientId === order.clientId && !deliveryReport));
    }
  }, [client, order, deliveryDate, deliveryReport]);

  const handleAddTeam = ({ teamId }) => {
    onTeamAdd({ ...deliveryDate, teamId });
    setShowSelect(false);
  };

  const handleAddPartner = ({ clientId }) => {
    onPartnerAdd({ ...deliveryDate, clientId });
    setShowSelect(false);
  };

  const handleAddUser = (user) => {
    onUserAdd(deliveryDate, user);
    setShowSelect(false);
  };

  const handleUpdate = (key, value, disableDispositionChecks = false) => {
    onUpdate({ ...deliveryDate, [key]: value }, key, OBJECT_DATE_TYPE_DELIVERY, disableDispositionChecks);
  };

  const renderAddUsers = () => {
    if (deliveryReport
        || !isAssignable
        || (order.clientId === client.clientId && deliveryDate.clientId)
        || order.state === ORDERSTATE_PARTNER_CREATED) {
      return null;
    }

    if (deliveryDate.clientId === client.clientId) {
      let caption = 'Team oder Monteur zuweisen';
      if (!deliveryDate.teamId) {
        if (deliveryDate.users && deliveryDate.users.length > 0) {
          caption = 'Weitere Monteure zuweisen';
        }
        return (
          <TextLink
            caption={caption}
            onPress={() => setShowSelect(true)}
            icon="fas fa-plus-square"
            style={{ marginTop: 20 }}
          />
        );
      }
    }

    if (deliveryDate.date && (!deliveryDate.teamId && !deliveryDate.clientId)) {
      let caption = 'Team/Monteur/Partner zuweisen';
      if (!canUsePartners) {
        caption = 'Team oder Monteur zuweisen';
      }

      if (deliveryDate.users && deliveryDate.users.length > 0) {
        caption = 'Weitere Monteure zuweisen';
      }

      return (
        <TextLink
          caption={caption}
          onPress={() => setShowSelect(true)}
          icon="fas fa-plus-square"
          style={{ marginTop: 20 }}
        />
      );
    }
    return null;
  };

  if (deliveryDate && object.splitted && order && order.internal && order.optionDelivery
    && (deliveryDate.clientId === client.clientId || order.clientId === client.clientId)
  ) {
    return (
      <Panel marginBottom={20} containerStyle={{ display: 'flex' }}>
        <div className="d-flex flex-row">
          <div className="flex-grow-1">
            <h6 className="Headline">
              <i className="fas fa-truck" style={{ marginRight: 10 }} />
              Lieferung
            </h6>
            <p>
              Bitte geben Sie hier das Lieferdatum an. Sobald Sie ein Datum festgelegt haben, können Sie ein
              Montageteam, Monteure oder einen Partner für die Lieferung zuweisen
            </p>
          </div>
          <div>
            <ObjectDateStatusBadge objectDateState={object.deliveryDate.state} />
          </div>
        </div>

        <KeyValueEditRow
          title="Lieferdatum"
          type="date"
          initialValue={deliveryDate.date}
          displayText={(deliveryDate.date) ? moment(deliveryDate.date).format('dddd DD.MM.YYYY') : ''}
          onChange={(allowEdit) ? (date) => handleUpdate('date', date) : null}
          onClear={(allowEdit) ? () => handleUpdate('date', null) : null}
          datePickerMaxDate={maxDate}
          datePickerMinDate={moment().format('YYYY-MM-DD')}
          editable={(isEditable && isTerminable)}
        />

        <KeyValueEditRow
          title="Lieferhinweis"
          type="textarea"
          initialValue={deliveryDate.comment}
          displayText={deliveryDate.comment}
          onChange={(allowEdit) ? (comment) => handleUpdate('comment', comment) : null}
        />

        <DispoInformation
          onChange={(key, value) => handleUpdate(key, value)}
          date={object.deliveryDate}
          object={object}
          order={order}
          client={client}
          editable={(isEditable && isTerminable)}
        />

        <TeamMonteurPartnerRow
          teamId={deliveryDate.teamId}
          partnerId={deliveryDate.clientId}
          users={deliveryDate.users}
          client={client}
          onDeleteTeam={() => handleUpdate('teamId', null, true)}
          onDeletePartner={() => handleUpdate('clientId', null, true)}
          onDeleteUser={(user) => dispatchDateDeleteUser(object, deliveryDate, user.userId)}
          hideUsersOrTeam={(deliveryDate.clientId && deliveryDate.clientId !== client.clientId)}
          disabled={(deliveryReport || !isAssignable)}
        />
        {renderAddUsers()}

        <SelectUserTeamPartner
          onAddTeam={handleAddTeam}
          onAddPartner={handleAddPartner}
          onAddUser={handleAddUser}
          visible={showSelect}
          alreadyAddedUsers={deliveryDate.users}
          showPartners={canUsePartners}
          showTeams={(deliveryDate.users.length === 0)}
          onClose={() => setShowSelect(false)}
          executionDate={deliveryDate.date}
        />
      </Panel>
    );
  }
  return null;
}

AssignDeliveryContainer.propTypes = {
  object: PropTypes.instanceOf(Object).isRequired,
  onUserAdd: PropTypes.func.isRequired,
  onTeamAdd: PropTypes.func.isRequired,
  onPartnerAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
